// War report (GDD §10.6, §13): the verdict in deadpan, ledger totals, cost per city captured, the national debt this
// war ran up, the seed (to replay the map), and a headline recap. A DOM overlay over the (idle) canvas.

import './results.css'
import { NEUTRAL_OWNER, OutcomeKind, type PlayerId } from '@taxwar/net'
import type { Screen, ScreenContext } from '../../app/screen'
import type { MatchResult, ResultAction, WarReport } from '../../match/contracts'
import type { VisualSettings } from '../../render/visualSettings'
import { button, canvasHost, el } from '../../hud/dom'
import { HudError } from '../../hud/errors'
import { formatClock, formatMoney, formatWhole, ticksToWholeSeconds } from '../../hud/format'
import { DESK_LABELS, HeadlineWriter } from '../../hud/headlines'
import { costPerCityCaptured, warSpend } from '../../hud/ledgerMath'
import { applyScreenTheme } from '../../hud/theme'
import { WarMapIndex } from '../../hud/warMap'
import type { TuningLink } from '../../tuning/link'

type Verdict = 'victory' | 'defeat' | 'draw'

interface VerdictCopy {
  readonly title: string
  /** Absurd system pop-up (GDD §13). */
  readonly popup: string
  readonly body: string
}

type OutcomeCopy =
  | { readonly kind: 'decisive'; readonly victory: VerdictCopy; readonly defeat: VerdictCopy }
  | { readonly kind: 'draw'; readonly draw: VerdictCopy }

type DecidedOutcome = Exclude<OutcomeKind, typeof OutcomeKind.none>

const OUTCOME_COPY: Readonly<Record<DecidedOutcome, OutcomeCopy>> = {
  [OutcomeKind.capitalDestroyed]: {
    kind: 'decisive',
    victory: {
      title: 'Victory',
      popup: 'VICTORY.EXE HAS STOPPED RESPONDING',
      body: 'The enemy capital is now a very expensive car park. Reconstruction contracts go out to tender in the morning.',
    },
    defeat: {
      title: 'Defeat',
      popup: 'CAPITAL_NOT_FOUND (404)',
      body: 'Your capital has been demolished. The Finance Minister notes that property prices have never been lower.',
    },
  },
  [OutcomeKind.regimeChange]: {
    kind: 'decisive',
    victory: {
      title: 'Victory',
      popup: 'REGIME.CHANGE — NOT OURS, FOR ONCE',
      body: 'Their public revolted before your tanks arrived. Your Press Secretary is taking full credit.',
    },
    defeat: {
      title: 'Regime change',
      popup: 'APPROVAL.DLL NOT FOUND',
      body: 'Approval hit zero and the public revolted. You have been relocated to a lucrative board seat.',
    },
  },
  [OutcomeKind.citiesMajority]: {
    kind: 'decisive',
    victory: {
      title: 'Victory on points',
      popup: 'MORE CITIES. MORE INVOICES.',
      body: 'Regulation expired with you holding more cities. Historians will call it a win; accountants will call it a quarter.',
    },
    defeat: {
      title: 'Defeat on points',
      popup: 'INSUFFICIENT CITIES',
      body: 'Time ran out and the enemy held more cities. Your maps have been updated accordingly.',
    },
  },
  [OutcomeKind.suddenDeath]: {
    kind: 'decisive',
    victory: {
      title: 'Sudden-death victory',
      popup: 'OVERTIME.BONUS APPROVED',
      body: 'First city captured in overtime. Double revenue, a one-city margin, triple the medals.',
    },
    defeat: {
      title: 'Sudden-death defeat',
      popup: 'OVERTIME.EXE HAS CRASHED',
      body: 'The enemy grabbed a city in overtime. The broadcast cut to adverts before anyone could explain.',
    },
  },
  [OutcomeKind.approvalTiebreak]: {
    kind: 'decisive',
    victory: {
      title: 'Victory by popularity',
      popup: 'LIKED.EXE',
      body: 'Cities were level, so approval decided it. Your public likes you slightly more than theirs likes them.',
    },
    defeat: {
      title: 'Defeat by popularity',
      popup: 'UNLIKED.EXE',
      body: 'Cities were level, so approval decided it. Theirs was higher. Consider lower taxes, or more propaganda.',
    },
  },
  [OutcomeKind.surrender]: {
    kind: 'decisive',
    victory: {
      title: 'Victory',
      popup: 'WHITE_FLAG.PNG RECEIVED',
      body: 'The enemy surrendered — or unplugged the router. The Treasury is counting it as a win either way.',
    },
    defeat: {
      title: 'Surrendered',
      popup: 'CONCESSION FILED IN TRIPLICATE',
      body: 'You conceded. The war has been reclassified as a strategic budget review.',
    },
  },
  [OutcomeKind.draw]: {
    kind: 'draw',
    draw: {
      title: 'Draw',
      popup: 'BOTH LEADERS DECLARED VICTORY',
      body: 'Cities tied, approval tied. Both governments announce a historic triumph and send each other the bill.',
    },
  },
}

const DEBT_REMARK = 'Payable by future generations in easy instalments.'
const NO_CAPTURES_LABEL = '— (no cities were acquired in the making of this invoice)'
const EMPTY_RECAP_TEXT = 'No headlines. The press was not invited.'
const BELLIGERENT_SEPARATOR = ' · '

function verdictOf(result: MatchResult, you: PlayerId): { readonly verdict: Verdict; readonly copy: VerdictCopy } {
  if (result.outcome === OutcomeKind.none) throw new HudError('war report for an undecided war')
  const copy = OUTCOME_COPY[result.outcome]
  if (copy.kind === 'draw') {
    if (result.winner !== NEUTRAL_OWNER) throw new HudError(`draw outcome ${result.outcome} names winner ${result.winner}`)
    return { verdict: 'draw', copy: copy.draw }
  }
  if (result.winner === NEUTRAL_OWNER) throw new HudError(`decisive outcome ${result.outcome} has no winner`)
  return result.winner === you ? { verdict: 'victory', copy: copy.victory } : { verdict: 'defeat', copy: copy.defeat }
}

/** Every nation in the war: yours, then each other seat in seat order. */
function belligerentsLine(map: WarMapIndex): string {
  return `${map.nationName(map.you)} vs ${map.enemies.map((seat) => map.nationName(seat)).join(BELLIGERENT_SEPARATOR)}`
}

function factRow(list: HTMLElement, label: string, value: string, valueClass = ''): void {
  list.append(el('dt', 'tw-dim', label), el('dd', valueClass, value))
}

export class ResultsScreen implements Screen {
  private readonly overlay: HTMLElement
  private readonly tuning: TuningLink

  /** `primary` and `secondary` are what comes next: a rematch or a new war (local), another rival or the menu (online). */
  constructor(context: ScreenContext, report: WarReport, primary: ResultAction, secondary: ResultAction) {
    const signal = context.signal
    const map = new WarMapIndex(report.setup)
    const { verdict, copy } = verdictOf(report.result, map.you)
    const ledger = report.finalPrivate.ledger

    this.overlay = el('div', `tw-screen tw-results tw-results--${verdict}`)
    this.tuning = context.tuning
    applyScreenTheme(this.overlay, context.tuning, context.settings)

    const header = el('header', 'tw-results__header')
    header.append(
      el('p', 'tw-results__popup', copy.popup),
      el('h1', 'tw-results__title', copy.title),
      el('p', 'tw-results__body', copy.body),
    )

    const facts = el('dl', 'tw-results__facts')
    factRow(facts, 'Belligerents', belligerentsLine(map))
    factRow(facts, 'War length', formatClock(ticksToWholeSeconds(report.result.tick)))
    factRow(facts, 'Seed', String(report.setup.seed), 'tw-results__seed')

    const totals = el('dl', 'tw-results__ledger')
    factRow(totals, 'Units', formatMoney(ledger.spentUnits))
    factRow(totals, 'Buildings', formatMoney(ledger.spentBuildings))
    factRow(totals, 'Strikes', formatMoney(ledger.spentStrikes))
    factRow(totals, 'Value lost in action', formatMoney(ledger.lostValue))
    factRow(totals, 'Cities acquired', formatWhole(ledger.citiesCaptured))
    factRow(totals, 'Cities misplaced', formatWhole(ledger.citiesLost))
    const perCity = costPerCityCaptured(ledger)
    factRow(totals, 'Cost per city captured', perCity === null ? NO_CAPTURES_LABEL : formatMoney(perCity))

    const debt = el('div', 'tw-results__debt')
    debt.append(
      el('span', 'tw-heading', 'National debt run up by this war'),
      el('span', 'tw-results__debt-value tw-money', formatMoney(warSpend(ledger))),
      el('span', 'tw-dim', DEBT_REMARK),
    )

    const ledgerPanel = el('section', 'tw-panel tw-results__panel')
    ledgerPanel.append(el('h2', 'tw-heading', 'War Ledger'), facts, totals, debt)

    const recapPanel = el('section', 'tw-panel tw-results__panel')
    recapPanel.append(el('h2', 'tw-heading', 'Headlines'), this.recapList(report, map))

    const columns = el('div', 'tw-results__columns')
    columns.append(ledgerPanel, recapPanel)

    const rematch = button('tw-button tw-button--primary', primary.label)
    rematch.addEventListener('click', primary.run, { signal })
    const newWar = button('tw-button', secondary.label)
    newWar.addEventListener('click', secondary.run, { signal })
    const actions = el('footer', 'tw-results__actions')
    actions.append(newWar, rematch)

    const frame = el('main', 'tw-results__frame')
    frame.append(header, columns, actions)
    this.overlay.append(frame)
    canvasHost(context.renderer).append(this.overlay)
    rematch.focus()
  }

  resize(): void {
    // The overlay is laid out by CSS; nothing is sized in code.
  }

  applySettings(settings: VisualSettings): void {
    applyScreenTheme(this.overlay, this.tuning, settings)
  }

  dispose(): void {
    this.overlay.remove()
  }

  private recapList(report: WarReport, map: WarMapIndex): HTMLElement {
    const writer = new HeadlineWriter(map)
    const list = el('ol', 'tw-results__recap')
    for (const event of report.recap) {
      const headline = writer.write(event)
      if (headline === null) continue
      const item = el('li', 'tw-results__headline')
      item.append(
        el('span', 'tw-results__time tw-dim', formatClock(ticksToWholeSeconds(headline.tick))),
        el('span', 'tw-results__desk', DESK_LABELS[headline.desk]),
        el('span', 'tw-results__text', headline.text),
      )
      list.append(item)
    }
    if (list.childElementCount === 0) list.append(el('li', 'tw-dim', EMPTY_RECAP_TEXT))
    return list
  }
}
